/**
 * ProjectStakeholderUpdate — the dated letter + Task Schedule to Completion
 * table that goes out to the hospital's project team. Renders in the email
 * body and attaches as a branded PDF. Every send is kept on the project.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Plus, Loader2, Sparkles, FileText, Send, ChevronLeft, Trash2, Save,
  Users, BookMarked, Lightbulb, X, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import { BulletEditor, SectionsEditor, TONE_BG } from "./updates/BulletEditor";

const STEPS = [
  { title: "1 · Open an update", body: "Start blank, or clone last month's so you only edit what changed." },
  { title: "2 · Draft it with AskIsaac", body: "AskIsaac writes the letter and the status bullets from the project phases, field logs and site survey. You edit every word." },
  { title: "3 · Edit the letter", body: "Salutation, intro paragraphs, Key updates, Next steps and the Timeline summary. Each bullet can be indented and highlighted yellow (pending), green (complete) or red (blocked)." },
  { title: "4 · Fix the schedule table", body: "The Task Schedule to Completion is seeded from your project phases. Set the owner, dates and the status block for each row." },
  { title: "5 · Check the recipients", body: "Pulled from the project team. Add named stakeholders (Biomed, Infection Prevention, Facilities) and they are saved for next time." },
  { title: "6 · Preview, then send", body: "Preview the PDF, then Send — the report goes in the email body and attaches as a PDF. The send is logged on the project." },
];

const TONE_BTNS = [
  { key: "none", cls: "bg-white border-slate-300" },
  { key: "yellow", cls: "bg-yellow-200 border-yellow-400" },
  { key: "green", cls: "bg-green-200 border-green-400" },
  { key: "red", cls: "bg-red-200 border-red-400" },
];

export default function ProjectStakeholderUpdate({ projectId, token, projectName = "" }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [meta, setMeta] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [showSend, setShowSend] = useState(false);
  const [showStake, setShowStake] = useState(false);
  const [send, setSend] = useState({ to: "", cc: "", subject: "", attach_pdf: true });
  const [stakeholders, setStakeholders] = useState([]);
  const [step, setStep] = useState(0);
  const [coach, setCoach] = useState(() => localStorage.getItem("pu_coach_off") !== "1");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/project-updates/projects/${projectId}/updates`, { headers });
      setMeta(r.data);
      setStakeholders(r.data.stakeholders || []);
    } catch { /* quiet */ } finally { setLoading(false); }
  }, [projectId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openUpdate = async (uid) => {
    try {
      const r = await axios.get(`${API}/project-updates/${uid}`, { headers });
      setDoc(r.data.update);
      setStep(1);
    } catch { toast.error("Could not open that update"); }
  };

  const create = async (mode) => {
    setBusy(true);
    try {
      const body = {};
      if (mode === "clone" && meta?.updates?.length) body.clone_update_id = meta.updates[0].id;
      if (mode?.startsWith?.("tpl:")) body.template_id = mode.slice(4);
      const r = await axios.post(`${API}/project-updates/projects/${projectId}/updates`, body, { headers });
      setDoc(r.data.update);
      setStep(1);
      load();
      toast.success("Draft opened — let AskIsaac write the first pass");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open a draft"); } finally { setBusy(false); }
  };

  const patch = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const save = async (silent = false) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/project-updates/${doc.id}`, {
        title: doc.title, date: doc.date, to_line: doc.to_line, from_line: doc.from_line,
        salutation: doc.salutation, intro_paragraphs: doc.intro_paragraphs,
        key_updates: doc.key_updates, next_steps: doc.next_steps,
        timeline_summary: doc.timeline_summary, closing: doc.closing,
        signature: doc.signature, schedule_rows: doc.schedule_rows,
        recipients: doc.recipients,
      }, { headers });
      setDoc(r.data.update);
      if (!silent) toast.success("Update saved");
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); return false; } finally { setBusy(false); }
  };

  const draftAi = async () => {
    setBusy(true);
    try {
      await save(true);
      const r = await axios.post(`${API}/project-updates/${doc.id}/draft-ai`, { notes: aiNotes }, { headers });
      setDoc(r.data.update);
      setStep(3);
      toast.success("AskIsaac drafted the letter — read it line by line before sending");
    } catch (e) { toast.error(e?.response?.data?.detail || "AskIsaac could not draft it"); } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      await save(true);
      const r = await axios.post(`${API}/project-updates/${doc.id}/send`, {
        to: send.to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: send.cc.split(",").map((s) => s.trim()).filter(Boolean),
        subject: send.subject, attach_pdf: send.attach_pdf,
      }, { headers });
      if (r.data.success) {
        toast.success("Update sent to stakeholders");
        setShowSend(false);
        openUpdate(doc.id);
        load();
      } else {
        toast.error(r.data.send?.error || "The mail provider rejected the message");
        openUpdate(doc.id);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };

  const saveStakeholders = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/project-updates/projects/${projectId}/stakeholders`, { stakeholders }, { headers });
      toast.success("Stakeholder list saved");
      setShowStake(false);
      load();
    } catch { toast.error("Could not save the stakeholder list"); } finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this template", `${projectName} monthly update`);
    if (!name) return;
    try {
      await axios.post(`${API}/project-updates/${doc.id}/save-as-template`, { name }, { headers });
      toast.success("Saved as a reusable template");
      load();
    } catch { toast.error("Could not save the template"); }
  };

  const Coach = () => {
    if (!coach) return null;
    const s = STEPS[Math.min(step, STEPS.length - 1)];
    return (
      <div className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 flex items-start gap-3"
        data-testid="update-coach">
        <Lightbulb className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-black uppercase tracking-wider text-sky-800">{s.title}</p>
          <p className="text-xs text-slate-700 mt-0.5">{s.body}</p>
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="update-coach-next"
              onClick={() => setStep((x) => Math.min(x + 1, STEPS.length - 1))}>
              Next step
            </Button>
            <button className="text-[11px] text-slate-500 hover:underline" data-testid="update-coach-off"
              onClick={() => { localStorage.setItem("pu_coach_off", "1"); setCoach(false); }}>
              Turn instructions off
            </button>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setCoach(false)}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  };

  // ------------------------------------------------------------- list view
  if (!doc) {
    return (
      <div className="space-y-4" data-testid="project-update-panel">
        <Coach />
        {!coach && (
          <button className="text-[11px] text-sky-700 hover:underline" data-testid="update-coach-on"
            onClick={() => { localStorage.removeItem("pu_coach_off"); setCoach(true); }}>
            Show step-by-step instructions
          </button>
        )}

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4 flex-wrap">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-sky-700">Stakeholder reporting</p>
              <h3 className="text-base font-black text-slate-900 inline-flex items-center gap-2">
                <Mail className="w-4 h-4 text-sky-700" /> Project update letter
              </h3>
              <p className="text-xs text-slate-500">
                The dated letter plus the Task Schedule to Completion — in the email body and attached as a PDF.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowStake(true)} data-testid="update-stakeholders-btn">
                <Users className="w-3.5 h-3.5 mr-1" /> Stakeholders ({(meta?.stakeholders || []).length})
              </Button>
              {!!(meta?.updates || []).length && (
                <Button size="sm" variant="outline" onClick={() => create("clone")} disabled={busy}
                  data-testid="update-clone-btn">
                  <BookMarked className="w-3.5 h-3.5 mr-1" /> Clone last update
                </Button>
              )}
              <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={() => create("blank")}
                disabled={busy} data-testid="update-new-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                New update
              </Button>
            </div>
          </div>

          <div className="p-4">
            {loading ? (
              <p className="text-sm text-slate-400 inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : !(meta?.updates || []).length ? (
              <p className="text-sm text-slate-400 py-3" data-testid="update-list-empty">
                No updates yet. Open one, let AskIsaac draft it off the project, then send it.
              </p>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="update-list">
                {meta.updates.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 py-2.5 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900">
                        {u.title}
                        {u.status === "sent" && (
                          <span className="ml-2 text-[9px] font-black uppercase tracking-wide text-white bg-emerald-600 rounded-full px-2 py-0.5">
                            sent
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {u.date} · {u.sends} send{u.sends === 1 ? "" : "s"}
                        {u.last_sent_at ? ` · last ${new Date(u.last_sent_at).toLocaleString()}` : ""}
                        {u.recipients?.length ? ` · ${u.recipients.join(", ")}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => openUpdate(u.id)}
                        data-testid={`update-open-${u.id}`}>Open</Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => openAuthedDoc(`/project-updates/${u.id}/preview`, { token, fmt: "pdf" })}>
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!!(meta?.templates || []).length && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Templates</span>
                {meta.templates.map((t) => (
                  <Button key={t.id} size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => create(`tpl:${t.id}`)} data-testid={`update-template-${t.id}`}>
                    {t.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <StakeholderDialog open={showStake} onOpenChange={setShowStake} stakeholders={stakeholders}
          setStakeholders={setStakeholders} onSave={saveStakeholders} busy={busy} team={meta?.team || []} />
      </div>
    );
  }

  // ----------------------------------------------------------- editor view
  const rows = doc.schedule_rows || [];
  const setRow = (i, patchObj) =>
    patch("schedule_rows", rows.map((r, j) => (j === i ? { ...r, ...patchObj } : r)));

  return (
    <div className="space-y-4" data-testid="project-update-editor">
      <Coach />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Button size="sm" variant="ghost" onClick={() => { setDoc(null); load(); }} data-testid="update-back-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-sky-700">
              Stakeholder update · {doc.id}
            </p>
            <h3 className="text-base font-black text-slate-900">{doc.title}</h3>
            {!!(doc.sends || []).length && (
              <p className="text-[11px] text-slate-500">
                {doc.sends.length} send(s) on record — last{" "}
                {new Date(doc.sends[doc.sends.length - 1].at).toLocaleString()}
                {doc.sends[doc.sends.length - 1].sent ? "" : " (failed)"}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={saveTemplate} data-testid="update-save-template">
            <BookMarked className="w-3.5 h-3.5 mr-1" /> Save as template
          </Button>
          <Button size="sm" variant="outline" onClick={() => save()} disabled={busy} data-testid="update-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
          <Button size="sm" variant="outline" data-testid="update-preview-btn"
            onClick={async () => { await save(true); openAuthedDoc(`/project-updates/${doc.id}/preview`, { token, fmt: "pdf" }); }}>
            <FileText className="w-3.5 h-3.5 mr-1" /> Preview PDF
          </Button>
          <Button size="sm" className="bg-sky-700 hover:bg-sky-800" data-testid="update-send-open-btn"
            onClick={() => {
              setSend({
                to: (doc.recipients?.to || []).join(", "), cc: (doc.recipients?.cc || []).join(", "),
                subject: `${doc.project_name} — project update ${doc.date}`, attach_pdf: true,
              });
              setStep(5); setShowSend(true);
            }}>
            <Send className="w-3.5 h-3.5 mr-1" /> Send to stakeholders
          </Button>
        </div>
      </div>

      {/* AI draft */}
      <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-3" data-testid="update-ai-box">
        <p className="text-[10px] font-black uppercase tracking-wider text-indigo-800 mb-1.5">
          Let AskIsaac write the first pass
        </p>
        <Textarea rows={2} className="bg-white text-xs" value={aiNotes}
          placeholder="Anything AskIsaac cannot know — what happened this week, what you are waiting on, who you need in the room…"
          onChange={(e) => setAiNotes(e.target.value)} data-testid="update-ai-notes" />
        <div className="flex justify-end mt-2">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={draftAi} disabled={busy}
            data-testid="update-ai-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Draft with AskIsaac
          </Button>
        </div>
        {doc.ai_error && <p className="text-[11px] text-red-600 mt-1">{doc.ai_error}</p>}
      </div>

      {/* letter header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
        <div className="grid sm:grid-cols-2 gap-2">
          <Field label="Title"><Input value={doc.title || ""} onChange={(e) => patch("title", e.target.value)} data-testid="update-title" /></Field>
          <Field label="Date"><Input type="date" value={(doc.date || "").slice(0, 10)} onChange={(e) => patch("date", e.target.value)} data-testid="update-date" /></Field>
          <Field label="To"><Input value={doc.to_line || ""} onChange={(e) => patch("to_line", e.target.value)} data-testid="update-to-line" /></Field>
          <Field label="From"><Input value={doc.from_line || ""} onChange={(e) => patch("from_line", e.target.value)} data-testid="update-from-line" /></Field>
          <Field label="Salutation"><Input value={doc.salutation || ""} placeholder="Tarek," onChange={(e) => patch("salutation", e.target.value)} data-testid="update-salutation" /></Field>
        </div>
        <Field label="Intro paragraphs — one per line">
          <Textarea rows={5} value={(doc.intro_paragraphs || []).join("\n")} data-testid="update-intro"
            onChange={(e) => patch("intro_paragraphs", e.target.value.split("\n").filter((x) => x.trim()))} />
        </Field>
      </div>

      <Block title="Key updates" testid="update-key-updates">
        <SectionsEditor sections={doc.key_updates || []} testid="update-key"
          onChange={(s) => patch("key_updates", s)} placeholder="e.g. Electrical and Plumbing Pre-Installation Work" />
      </Block>

      <Block title="Next steps" testid="update-next-steps">
        <SectionsEditor sections={doc.next_steps || []} testid="update-next"
          onChange={(s) => patch("next_steps", s)} placeholder="e.g. Phase Two — Additional Components" />
      </Block>

      <Block title="Timeline summary" testid="update-timeline">
        <div className="space-y-1">
          {(doc.timeline_summary || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`update-timeline-row-${i}`}>
              <Input className="h-8 text-xs w-40" placeholder="12/13" value={t.when || ""}
                onChange={(e) => patch("timeline_summary", doc.timeline_summary.map((x, j) => (j === i ? { ...x, when: e.target.value } : x)))} />
              <Input className="h-8 text-xs" placeholder="Containment installation completed." value={t.what || ""}
                onChange={(e) => patch("timeline_summary", doc.timeline_summary.map((x, j) => (j === i ? { ...x, what: e.target.value } : x)))} />
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                onClick={() => patch("timeline_summary", doc.timeline_summary.filter((_, j) => j !== i))}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="update-timeline-add"
            onClick={() => patch("timeline_summary", [...(doc.timeline_summary || []), { when: "", what: "" }])}>
            <Plus className="w-3 h-3 mr-1" /> Timeline line
          </Button>
        </div>
      </Block>

      {/* schedule table */}
      <Block title="Task schedule to completion"
        hint="Seeded from the project phases — set the owner, dates and the status block per task"
        testid="update-schedule">
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.id || i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`update-row-${i}`}>
              <div className="grid sm:grid-cols-4 gap-2">
                <Input className="h-8 text-xs font-semibold" placeholder="Task" value={r.task || ""}
                  onChange={(e) => setRow(i, { task: e.target.value })} data-testid={`update-row-task-${i}`} />
                <Input className="h-8 text-xs" placeholder="Owner" value={r.owner || ""}
                  onChange={(e) => setRow(i, { owner: e.target.value })} data-testid={`update-row-owner-${i}`} />
                <Input className="h-8 text-xs" placeholder="Start date" value={r.start_date || ""}
                  onChange={(e) => setRow(i, { start_date: e.target.value })} data-testid={`update-row-start-${i}`} />
                <Input className="h-8 text-xs" placeholder="Completion date" value={r.completion_date || ""}
                  onChange={(e) => setRow(i, { completion_date: e.target.value })} data-testid={`update-row-end-${i}`} />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input className={`h-8 text-xs font-semibold ${TONE_BG[r.status_tone] || ""}`}
                  placeholder="Status headline — e.g. Remaining work estimated completion 12/27"
                  value={r.status_title || ""} onChange={(e) => setRow(i, { status_title: e.target.value })}
                  data-testid={`update-row-status-${i}`} />
                <div className="flex items-center gap-0.5">
                  {TONE_BTNS.map((t) => (
                    <button key={t.key} type="button"
                      className={`w-4 h-4 rounded-sm border ${t.cls} ${(r.status_tone || "none") === t.key ? "ring-2 ring-slate-900" : ""}`}
                      onClick={() => setRow(i, { status_tone: t.key })}
                      data-testid={`update-row-tone-${i}-${t.key}`} />
                  ))}
                </div>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                  onClick={() => patch("schedule_rows", rows.filter((_, j) => j !== i))}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="mt-2">
                <BulletEditor bullets={r.bullets || []} testid={`update-row-bullets-${i}`}
                  onChange={(b) => setRow(i, { bullets: b })} />
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="update-row-add"
            onClick={() => patch("schedule_rows", [...rows, { task: "", owner: "", start_date: "", completion_date: "", status_title: "", status_tone: "none", bullets: [] }])}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Task row
          </Button>
        </div>
      </Block>

      <Block title="Closing & signature" testid="update-closing">
        <Textarea rows={3} value={doc.closing || ""} data-testid="update-closing-text"
          placeholder="If you have any questions or concerns regarding the timeline…"
          onChange={(e) => patch("closing", e.target.value)} />
        <div className="grid sm:grid-cols-4 gap-2 mt-2">
          {["name", "title", "phone", "email"].map((k) => (
            <Input key={k} className="h-8 text-xs" placeholder={k} value={(doc.signature || {})[k] || ""}
              onChange={(e) => patch("signature", { ...(doc.signature || {}), [k]: e.target.value })}
              data-testid={`update-sig-${k}`} />
          ))}
        </div>
      </Block>

      {/* send */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent className="max-w-lg" data-testid="update-send-dialog">
          <DialogHeader><DialogTitle>Send the update</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder="To — comma separated" value={send.to}
              onChange={(e) => setSend((s) => ({ ...s, to: e.target.value }))} data-testid="update-send-to" />
            <Input placeholder="CC" value={send.cc}
              onChange={(e) => setSend((s) => ({ ...s, cc: e.target.value }))} data-testid="update-send-cc" />
            <Input placeholder="Subject" value={send.subject}
              onChange={(e) => setSend((s) => ({ ...s, subject: e.target.value }))} data-testid="update-send-subject" />
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={send.attach_pdf} data-testid="update-send-attach"
                onChange={(e) => setSend((s) => ({ ...s, attach_pdf: e.target.checked }))} />
              Attach the branded PDF as well as putting the report in the email body
            </label>
            {!!(meta?.team || []).length && (
              <p className="text-[11px] text-slate-500">
                Project team on file: {(meta.team || []).map((t) => t.email).join(", ")}
              </p>
            )}
            <div className="flex justify-end">
              <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={doSend} disabled={busy}
                data-testid="update-send-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StakeholderDialog open={showStake} onOpenChange={setShowStake} stakeholders={stakeholders}
        setStakeholders={setStakeholders} onSave={saveStakeholders} busy={busy} team={meta?.team || []} />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </div>
);

const Block = ({ title, hint, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{title}</p>
    {hint && <p className="text-[11px] text-slate-400 mb-2">{hint}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

const StakeholderDialog = ({ open, onOpenChange, stakeholders, setStakeholders, onSave, busy, team }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="update-stakeholder-dialog">
      <DialogHeader><DialogTitle>Named stakeholders for this project</DialogTitle></DialogHeader>
      <div className="space-y-2">
        <p className="text-xs text-slate-500">
          Biomed, Infection Prevention, Facilities, the owner's PM. Saved on the project and
          pre-filled on every future update. The project team is pulled in automatically.
        </p>
        {stakeholders.map((s, i) => (
          <div key={i} className="grid sm:grid-cols-5 gap-2 items-center" data-testid={`stakeholder-row-${i}`}>
            <Input className="h-8 text-xs" placeholder="Name" value={s.name || ""}
              onChange={(e) => setStakeholders(stakeholders.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <Input className="h-8 text-xs" placeholder="Email" value={s.email || ""}
              onChange={(e) => setStakeholders(stakeholders.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
            <Input className="h-8 text-xs" placeholder="Role — Biomed / IP" value={s.role || ""}
              onChange={(e) => setStakeholders(stakeholders.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} />
            <Input className="h-8 text-xs" placeholder="Organization" value={s.organization || ""}
              onChange={(e) => setStakeholders(stakeholders.map((x, j) => (j === i ? { ...x, organization: e.target.value } : x)))} />
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-slate-600 inline-flex items-center gap-1">
                <input type="checkbox" checked={!!s.cc_only}
                  onChange={(e) => setStakeholders(stakeholders.map((x, j) => (j === i ? { ...x, cc_only: e.target.checked } : x)))} />
                CC
              </label>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                onClick={() => setStakeholders(stakeholders.filter((_, j) => j !== i))}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="stakeholder-add"
              onClick={() => setStakeholders([...stakeholders, { name: "", email: "", role: "", organization: "", cc_only: false }])}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Stakeholder
            </Button>
            {!!team.length && (
              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="stakeholder-import-team"
                onClick={() => setStakeholders([
                  ...stakeholders,
                  ...team.filter((t) => !stakeholders.some((s) => s.email === t.email))
                    .map((t) => ({ name: t.name || "", email: t.email, role: t.role || "", organization: t.organization || "", cc_only: false })),
                ])}>
                <Users className="w-3.5 h-3.5 mr-1" /> Pull in the project team
              </Button>
            )}
          </div>
          <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={onSave} disabled={busy}
            data-testid="stakeholder-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            Save list
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
